import { useEffect, useRef, useState } from 'react';
import { OrderConflictError, updateOrderNotes } from '../../lib/orders';

type NotesField = 'pickupNotes' | 'deliveryNotes';

interface EditableNotesProps {
  orderId: string;
  field: NotesField;
  value: string;
  expectedLastModifiedDate: string;
  canEdit: boolean;
  onSaved: (nieuweLastModifiedDate: string | null, newValue: string) => void;
}

export function EditableNotes({
  orderId,
  field,
  value,
  expectedLastModifiedDate,
  canEdit,
  onSaved,
}: EditableNotesProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [editBase, setEditBase] = useState({ orderId, field, value, lastModifiedDate: expectedLastModifiedDate });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingSave = useRef(false);
  const mounted = useRef(true);
  const identity = useRef({ orderId, field, generation: 0 });
  if (identity.current.orderId !== orderId || identity.current.field !== field) {
    identity.current = { orderId, field, generation: identity.current.generation + 1 };
  }

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => { setEditing(false); setError(null); }, [orderId, field]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      const end = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(end, end);
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(value);
    setEditBase({ orderId, field, value, lastModifiedDate: expectedLastModifiedDate });
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
    setError(null);
  };

  const save = async () => {
    // A keyboard shortcut can fire twice before React renders disabled=true.
    if (pendingSave.current || !editing || editBase.orderId !== orderId || editBase.field !== field) return;
    const trimmed = draft.trim();
    if (trimmed === editBase.value.trim()) {
      setEditing(false);
      return;
    }
    const generation = identity.current.generation;
    const stillCurrent = () => mounted.current && identity.current.generation === generation;
    pendingSave.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await updateOrderNotes(
        editBase.orderId,
        { [editBase.field]: trimmed.length === 0 ? null : trimmed },
        { [editBase.field]: editBase.value },
        editBase.lastModifiedDate || undefined
      );
      // The write belongs to its original order. Never apply its response to
      // a different detail view (including away-and-back while in flight).
      if (stillCurrent()) {
        onSaved(result.nieuweLastModifiedDate, trimmed);
        setEditing(false);
      }
    } catch (err) {
      if (!stillCurrent()) return;
      if (err instanceof OrderConflictError) {
        setError('Order is ondertussen gewijzigd. Annuleer en herlaad.');
      } else {
        setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      }
    } finally {
      pendingSave.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void save();
    }
  };

  if (!editing) {
    const hasValue = value.trim().length > 0;
    return (
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-medium text-slate-500">Opmerkingen</p>
          {canEdit && (
            <button
              type="button"
              onClick={startEdit}
              className="text-xs text-indigo-600 hover:underline"
            >
              {hasValue ? 'Wijzigen' : 'Toevoegen'}
            </button>
          )}
        </div>
        {hasValue ? (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>
        ) : (
          <p className="text-sm text-slate-400 italic">Geen opmerkingen</p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-xs font-medium text-slate-500 mb-1">Opmerkingen</p>
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={saving}
        rows={3}
        className="w-full text-sm text-slate-900 border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
        placeholder="Bijv. aanbellen bij buren, code poort 1234..."
      />
      {error && (
        <p className="mt-1 text-xs text-amber-700">⚠ {error}</p>
      )}
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
        >
          Annuleren
        </button>
        <span className="text-[10px] text-slate-400 ml-auto">⌘+Enter / Esc</span>
      </div>
    </div>
  );
}
